import { Controller } from 'stimulus'
import Rails from '@rails/ujs'
import ColorUtils from '../../src/javascripts/colour-utils.js'

export default class extends Controller {
  static targets = [
    'preview',
    'previewLoading',
    'mobilePreviewHeader',
    'mobilePreviewBody',
    'mobilePreviewBurgerMenu',
    'mobilePreviewLogo',
    'inlineName',
    'nameInput',
    'primaryColourInput',
    'primaryColourBlock',
    'secondaryColourInput',
    'secondaryColourBlock',
    'styleSelectForm',
    'styleSelectId',
    'createInput',
    'innerCreateButton',
    'listItem',
    'newStyleInput'
  ]

  static values = {
    remoteLoad: Boolean,
    name: String,
    primaryColour: String,
    secondaryColour: String,
    logo: String,
    resetConfirm: String
  }

  connect() {
    this.nameValueOriginal = this.nameValue
    this.primaryColourValueOriginal = this.primaryColourValue
    this.secondaryColourValueOriginal = this.secondaryColourValue

    this.configureForm()
    if (this.remoteLoadValue) document.dispatchEvent(new Event('uppy:reinit'))
  }

  configureForm() {
    this.updateColourBlocks()
    this.showColourBlocks()
    this.updateMobilePreview()
    window.tippy('[data-tippy-content]')
  }

  updateMobilePreview() {
    this.updateMobilePreviewHeader()
    this.updateMobilePreviewBody()
    this.updateMobilePreviewBurgerMenu()
    this.showPreview()
  }

  showPreview() {
    this.previewLoadingTarget.classList.add('hidden')
    this.previewTarget.classList.remove('hidden')
  }

  hidePreview() {
    this.previewTarget.classList.add('hidden')
    this.previewLoadingTarget.classList.remove('hidden')
  }

  logoImageLoaded(event) {
    this.mobilePreviewLogoTarget.setAttribute('src', event.target.getAttribute('src'))
  }

  updateName(event) {
    this.nameValue = event.target.value
    this.updateInlineName()
  }

  updateInlineName() {
    if (this.hasInlineNameTarget) this.inlineNameTarget.textContent = this.nameValue
  }

  inputPrimaryColour() {
    let value = this.primaryColourBlockTarget.value
    if (value && !value.startsWith('#')) value = `#${value}`
    value = value.substring(0, 7).toUpperCase()

    this.primaryColourBlockTarget.value = value
    if (value.length === 7) {
      this.primaryColourValue = value
      this.primaryColourInputTarget.value = value
      this.updateColourBlocks()
      this.updateMobilePreview()
    }
  }

  inputSecondaryColour() {
    let value = this.secondaryColourBlockTarget.value
    if (value && !value.startsWith('#')) value = `#${value}`
    value = value.substring(0, 7).toUpperCase()

    this.secondaryColourBlockTarget.value = value
    if (value.length === 7) {
      this.secondaryColourValue = value
      this.secondaryColourInputTarget.value = value
      this.updateColourBlocks()
      this.updateMobilePreview()
    }
  }

  updatePrimaryColour(event) {
    this.primaryColourValue = event.target.value
    this.updateColourBlocks()
    this.updateMobilePreview()
  }

  updateSecondaryColour(event) {
    this.secondaryColourValue = event.target.value
    this.updateColourBlocks()
    this.updateMobilePreview()
  }

  updateColourBlocks() {
    this.updatePrimaryColourBlock()
    this.updateSecondaryColourBlock()
  }

  showColourBlocks() {
    this.primaryColourBlockTarget.classList.remove('invisible')
    this.secondaryColourBlockTarget.classList.remove('invisible')
  }

  updatePrimaryColourBlock() {
    this.primaryColourBlockTarget.value = this.primaryColourValue.toUpperCase()
    this.primaryColourBlockTarget.style.backgroundColor = this.primaryColourValue
    this.primaryColourBlockTarget.style.color = ColorUtils.contrastingColor(this.primaryColourValue)
  }

  updateSecondaryColourBlock() {
    this.secondaryColourBlockTarget.value = this.secondaryColourValue.toUpperCase()
    this.secondaryColourBlockTarget.style.backgroundColor = this.secondaryColourValue
    this.secondaryColourBlockTarget.style.color = ColorUtils.contrastingColor(this.secondaryColourValue)
  }

  updateMobilePreviewHeader() {
    this.mobilePreviewHeaderTarget.style.backgroundColor = this.primaryColourValue
  }

  updateMobilePreviewBody() {
    this.mobilePreviewBodyTarget.style.backgroundColor = this.secondaryColourValue
  }

  updateMobilePreviewBurgerMenu() {
    this.mobilePreviewBurgerMenuTarget.setAttribute('stroke', ColorUtils.contrastingColor(this.primaryColourValue))
  }

  resetStyle(event) {
    event.preventDefault()
    if (!confirm(this.resetConfirmValue || 'Are you sure you want to reset this style?')) return

    this.nameValue = this.nameValueOriginal
    this.nameInputTarget.value = this.nameValue
    this.updateInlineName()

    this.primaryColourValue = this.primaryColourValueOriginal
    this.secondaryColourValue = this.secondaryColourValueOriginal
    this.updateColourBlocks()
    this.updateMobilePreview()
  }

  select(event) {
    event.preventDefault()

    const styleId = event.currentTarget.dataset.styleId
    this.selectListItem(styleId)
    this.styleSelectIdTarget.value = styleId
    this.hidePreview()
    Rails.fire(this.styleSelectFormTarget, 'submit')
  }

  selectListItem(styleId) {
    this.listItemTargets.forEach((item) => {
      const selected = item.dataset.styleId === styleId
      item.querySelector('.dot').classList.toggle('hidden', !selected)
      item.querySelector('.select-btn').classList.toggle('hidden', selected)
      const trash = item.querySelector('.trash')
      if (trash) trash.classList.toggle('hidden', selected)
      if (selected) {
        item.setAttribute('aria-current', 'true')
      } else {
        item.removeAttribute('aria-current')
      }
    })
  }

  delete(event) {
    event.preventDefault()

    const item = event.currentTarget.closest('.selector_item')
    if (!confirm(`Are you sure you wish to delete the ${item.dataset.name} style?`)) return

    Rails.ajax({
      url: item.dataset.deleteUrl,
      type: 'delete',
      success: () => {
        item.remove()
        this.renumberItems()
      }
    })
  }

  renumberItems() {
    this.listItemTargets.forEach((item, index) => {
      item.querySelector('.selector_item_index').textContent = index + 1
    })
  }

  showCreateInput(event) {
    event.preventDefault()
    if (!this.hasCreateInputTarget || !this.hasInnerCreateButtonTarget) return

    this.innerCreateButtonTarget.classList.add('hidden')
    this.createInputTarget.classList.remove('hidden')
    setTimeout(() => this.newStyleInputTarget.focus(), 750)
  }

  hideCreateInput(event) {
    event.preventDefault()
    if (!this.hasCreateInputTarget || !this.hasInnerCreateButtonTarget) return

    this.createInputTarget.classList.add('hidden')
    this.innerCreateButtonTarget.classList.remove('hidden')
  }
}
